import { AsyncStatus, MetaConversation } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import findIndex from 'lodash/findIndex';
import { handleActions } from 'redux-actions';

import { emptyMetaConversation } from '../../../conversation/reducers/conversation';
import { transferToAgentActions } from '../actions/transferToAgentModal';

export type TransferRequestState = {
  metaConversation: MetaConversation | null;
  requestToUser: User;
  requestFromAgent: User;
};

export type TransferModalState = {
  meta: {
    requestAssistedTransfer: AsyncStatus;
    requestBlindTransfer: AsyncStatus;
  };
  ui: {
    isModalShown: boolean;
    isMessageModalShown: boolean;
  };
  transferRequests: Array<TransferRequestState>;
  metaConversation: MetaConversation;
  queueId: string | null;
};

export type CauseT = 'rejected' | 'takeback' | 'timeout' | 'accepted';

export const initialState: TransferModalState = {
  meta: {
    requestAssistedTransfer: 'succeeded',
    requestBlindTransfer: 'succeeded',
  },
  ui: {
    isModalShown: false,
    isMessageModalShown: false,
  },
  metaConversation: emptyMetaConversation(),
  transferRequests: [],
  queueId: null,
};

export const {
  transferToAgentAssistedRequest,
  transferToAgentAssistedRequestSucceeded,
  transferToAgentAssistedRequestFailed,

  transferToAgentBlindRequest,
  transferToAgentBlindRequestSucceeded,
  transferToAgentBlindRequestFailed,

  transferToAgentAssistedResolve,
  transferToAgentAssistedResolveSucceeded,
  transferToAgentAssistedResolveFailed,

  transferToAgentModalOpen,
  transferToAgentModalClose,

  transferToAgentRequestStop,

  transferToAgentShowMessageModal,
  transferToAgentHideMessageModal,

  // WS actions.
  transferToAgentWsResponse,
  transferToAgentWsTransferStarted,
  transferToAgentWsTransferAnswered,
} = transferToAgentActions;

const transferToAgent = handleActions<TransferModalState, any>(
  {
    [transferToAgentShowMessageModal.toString()]: (
      state: TransferModalState,
    ) => {
      return { ...state, ui: { ...state.ui, isMessageModalShown: true } };
    },

    [transferToAgentHideMessageModal.toString()]: (
      state: TransferModalState,
    ) => {
      return { ...state, ui: { ...state.ui, isMessageModalShown: false } };
    },

    // Show transfer to agent modal.
    [transferToAgentModalOpen.toString()]: (
      state: TransferModalState,
      action: {
        payload: {
          metaConversation: MetaConversation;
          queueId?: string;
        };
      },
    ) => {
      const metaConversation = Object.assign(
        {},
        action.payload.metaConversation,
      );
      return {
        ...state,
        ui: { ...state.ui, isModalShown: true },
        metaConversation: metaConversation,
        queueId: action.payload.queueId ?? null,
      };
    },

    // Hide transfer to agent modal.
    [transferToAgentModalClose.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        ui: { ...state.ui, isModalShown: false },
        users: [],
        queueId: null,
      };
    },

    [transferToAgentAssistedRequest.toString()]: (
      state: TransferModalState,
      action: {
        payload: {
          metaConversation: MetaConversation;
          requestFromAgent: User;
          requestToUser: User;
        };
      },
    ) => {
      const metaConversation = Object.assign(
        {},
        action.payload.metaConversation,
      );
      const requestToUser = Object.assign({}, action.payload.requestToUser);
      const requestFromAgent = Object.assign(
        {},
        action.payload.requestFromAgent,
      );

      const request = {
        metaConversation: metaConversation,
        requestToUser: requestToUser,
        requestFromAgent: requestFromAgent,
      };
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'loading' },
        transferRequests: [
          request,
          ...state.transferRequests.filter(
            (t: any) =>
              t.metaConversation.conversation.id !==
              metaConversation.conversation.id,
          ),
        ],
      };
    },

    // We do not remove metaConversation here,
    // we need to wait until transferTAgentAssistedResolveSucceeded/Failed.
    // or transferToAgentAssistedRequestFailed
    [transferToAgentAssistedRequestSucceeded.toString()]: (
      state: TransferModalState,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'succeeded' },
        ui: { ...state.ui, isModalShown: false },
        users: [],
      };
    },

    // The transfer to agent request failed.
    // It does not make sense to keep the metaConversation in the store.
    [transferToAgentAssistedRequestFailed.toString()]: (
      state: TransferModalState,
      action: { payload: { metaConversation: MetaConversation; action: any } },
    ) => {
      const { metaConversation } = action.payload.action;
      const index = findIndex(
        state.transferRequests,
        (assistedTransferRequest: TransferRequestState) => {
          return (
            metaConversation !== undefined &&
            assistedTransferRequest?.metaConversation?.conversation.id ===
              metaConversation.conversation.id
          );
        },
      );
      if (index > -1) {
        const transferRequests = state.transferRequests.slice();
        transferRequests.splice(index, 1);
        return {
          ...state,
          meta: { ...state.meta, requestAssistedTransfer: 'failed' },
          transferRequests: transferRequests,
          metaConversation: emptyMetaConversation(),
        };
      }
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'failed' },
        metaConversation: emptyMetaConversation(),
      };
    },

    [transferToAgentBlindRequest.toString()]: (
      state: TransferModalState,
      action: {
        payload: {
          metaConversation: MetaConversation;
          requestFromAgent: User;
          requestToUser: User;
        };
      },
    ) => {
      const metaConversation = Object.assign(
        {},
        action.payload.metaConversation,
      );
      const requestToUser = Object.assign({}, action.payload.requestToUser);
      const requestFromAgent = Object.assign(
        {},
        action.payload.requestFromAgent,
      );

      const request = {
        metaConversation: metaConversation,
        requestToUser: requestToUser,
        requestFromAgent: requestFromAgent,
      };
      return {
        ...state,
        meta: { ...state.meta, requestBlindTransfer: 'loading' },
        transferRequests: [
          request,
          ...state.transferRequests.filter(
            (t: any) =>
              t.metaConversation.conversation.id !==
              metaConversation.conversation.id,
          ),
        ],
      };
    },

    // We do not remove the metaConversation yet.
    [transferToAgentBlindRequestSucceeded.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestBlindTransfer: 'succeeded' },
        ui: { ...state.ui, isModalShown: false },
        users: [],
      };
    },

    [transferToAgentBlindRequestFailed.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      const { metaConversation } = action.payload.action;
      const index = findIndex(
        state.transferRequests,
        (blindTransferRequest: TransferRequestState) => {
          return (
            metaConversation !== undefined &&
            blindTransferRequest?.metaConversation?.conversation?.id ===
              metaConversation.conversation.id
          );
        },
      );
      if (index > -1) {
        const transferRequests = state.transferRequests.slice();
        transferRequests.splice(index, 1);
        return {
          ...state,
          meta: { ...state.meta, requestBlindTransfer: 'failed' },
          transferRequests: transferRequests,
        };
      }
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'failed' },
      };
    },

    [transferToAgentAssistedResolve.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'loading' },
      };
    },

    // The transfer to agent was resolved.
    // We cleanup the store (by removing the metaConversation).
    [transferToAgentAssistedResolveSucceeded.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      const csid = action.payload.data.csid;

      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'succeeded' },
        transferRequests: [
          ...state.transferRequests.filter(
            (t: any) => t.metaConversation.conversation.id !== csid,
          ),
        ],
        metaConversation: emptyMetaConversation(),
      };
    },

    [transferToAgentAssistedResolveFailed.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      return {
        ...state,
        meta: { ...state.meta, requestAssistedTransfer: 'failed' },
      };
    },

    // The transfer to agent request was stop (maybe because the conversation was closed/claimed).
    // We remove the metaConversation from the store and closes the transfer modal.
    [transferToAgentRequestStop.toString()]: (
      state: TransferModalState,
      action,
    ) => {
      const { csid } = action.payload;
      const transferRequests = state.transferRequests.slice();
      const index = findIndex(state.transferRequests, (request) => {
        return request.metaConversation?.conversation.id === csid;
      });
      if (index > -1) {
        transferRequests.splice(index, 1);
        return {
          ...state,
          transferRequests: transferRequests,
          metaConversation: emptyMetaConversation(),
        };
      }
      return {
        ...state,
        ui: { ...state.ui, isModalShown: false },
        users: [],
        metaConversation: emptyMetaConversation(),
      };
    },

    [transferToAgentWsResponse.toString()]: (
      state: TransferModalState,
      action: any,
    ) => {
      const { csid, accepted } = action.payload;
      const transferRequests = state.transferRequests.slice();

      const index = findIndex(state.transferRequests, (request) => {
        return (
          request.metaConversation?.conversation.id === csid &&
          (accepted === true || accepted === false)
        );
      });

      const ui = Object.assign({}, state.ui);

      // If the modal is open and the transfer is completed
      if (
        ui.isModalShown === true &&
        (accepted === true || accepted === false) &&
        csid === state.metaConversation.conversation.id
      ) {
        ui.isModalShown = false;
      }

      if (index > -1) {
        transferRequests.splice(index, 1);
        return {
          ...state,
          ui: ui,
          transferRequests: transferRequests,
          metaConversation: emptyMetaConversation(),
        };
      }
      return { ...state, ui: ui };
    },
  },
  initialState,
);

export default transferToAgent;
